import {useDynamicSettings} from "@montana/containers/dynamic-settings";
import {useSettings} from "@montana/containers/settings/settings.container";
import {LayoutContext} from "@montana/context/layout.context";
import {ILanguage, IMarket} from "@montana/models/generated/cms.interface";
import {Button} from "@montana/shared/button";
import {Container} from "@montana/shared/container";
import {Select} from "@montana/shared/form";
import {IFieldOptionValue} from "@montana/shared/form/field/field";
import {MarketButton} from "@montana/shared/market-button/market-button";
import {SvgIcon} from "@montana/shared/svg-icon";
import clsx from "clsx";
import {AnimatePresence, motion} from "framer-motion";
import React, {useContext, useMemo, useState} from "react";
import {isBrowser} from "@montana/helpers/browser.helper";
import {useTranslations} from "@montana/containers/translations";
import {useMediaQuery} from "@montana/hooks/useMediaQuery";
import {usePreventScroll} from "@react-aria/overlays";
import {getLanguageSelectorLanguage, getLanguageSelectorOptions} from "@montana/helpers/market.helper";

/**
 * @description Component to display the language selector on desktop.
 * @author HBM
 */

export function LanguageAndMarketSelector() {
    const { market, language, changeLanguageAndMarket } = useSettings();
    const { markets, languages, completeLanguageList } = useDynamicSettings();
    const { isLanguageSelectorOpen, setLanguageSelectorOpen } = useContext(
        LayoutContext
    );
    const selectedLanguage = getLanguageSelectorLanguage(languages, language, completeLanguageList);
    const [state, setState] = useState<{ market: IMarket; language: ILanguage }>({
        market: market!,
        language: selectedLanguage!
    });
    const isMobile = useMediaQuery('(max-width: 768px)');

    const translate = useTranslations();

    usePreventScroll({
        isDisabled: !isLanguageSelectorOpen,
    });

    const url = isBrowser ? window.location.href : '';

    const marketOptions = useMemo<IFieldOptionValue[] | null>(
        () =>
            markets
                ? markets
                    .filter(market => !market.hideInMarketSelector)
                    .map(market => ({
                        text: `${market.displayName} / ${market.currency}`,
                        element: <span className="select-option__wrapper"><span className="select-option__emphazied">{market.displayName}</span><span>{market.marketId?.toLowerCase() !== 'en' ? market.currency : ''}</span></span>,
                        value: market.marketId!
                    }))
                : null,
        [markets]
    );

    const languageOptions = useMemo<IFieldOptionValue[] | null>(
        () =>
            getLanguageSelectorOptions(languages, language, completeLanguageList),
        [completeLanguageList, language, languages]
    );

    const toggleLanguageSelector = () => {
        window.scrollTo(0, document.body.scrollHeight);
        setLanguageSelectorOpen?.(!isLanguageSelectorOpen);
    };

    return (
        <>
            <MarketButton
                onClick={toggleLanguageSelector}
                isOpen={isLanguageSelectorOpen}
            />
            <AnimatePresence initial={false}>
                {isLanguageSelectorOpen ? (
                    <>
                        <motion.div
                            onClick={toggleLanguageSelector}
                            className="language-and-market-selector__overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        />
                        <motion.div
                            className={clsx("language-and-market-selector", {
                                "language-and-market-selector--mobile": isMobile
                            })}
                            initial={{ translateY: isMobile ? 400 : 220 }}
                            animate={{ translateY: 0 }}
                            exit={{ translateY: isMobile ? 400 : 220 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            style={{ height: isMobile ? 400 : 220 }}
                        >
                            <Container className="language-and-market-selector__container">
                                <button
                                    onClick={toggleLanguageSelector}
                                    className="language-and-market-selector__close"
                                >
                                    <SvgIcon
                                        className="language-and-market-selector__close-icon"
                                        iconName="close"
                                    />
                                </button>
                                <section className="language-and-market-selector__content">
                                    <h3 className="language-and-market-selector__text">
                                        {translate(t => t.languageAndMarkedSelector.title, "Select your language and market")}
                                    </h3>
                                    {marketOptions ? (
                                        <Select
                                            isValid
                                            onChange={e =>
                                                setState({
                                                    ...state,
                                                    market: markets?.find(
                                                        market => market.marketId === e
                                                    )!
                                                })
                                            }
                                            value={market?.marketId}
                                            label={translate(t => t.languageAndMarkedSelector.countryLabel, "Country")}
                                            options={marketOptions}
                                            direction="up"
                                        />
                                    ) : null}
                                    {languageOptions ? (
                                        <Select
                                            isValid
                                            onChange={(value) =>
                                                setState({
                                                    ...state,
                                                    language: languages?.find(
                                                        language => language.languageURLSegment === value
                                                    ) || getLanguageSelectorLanguage(languages, value, completeLanguageList)!
                                                })
                                            }
                                            value={language}
                                            label={translate(t => t.languageAndMarkedSelector.languageLabel, "Language")}
                                            options={languageOptions}
                                            direction="up"
                                        />
                                    ) : null}
                                    <Button
                                        onClick={() =>
                                            changeLanguageAndMarket(state.language?.languageURLSegment!, state.market, url)
                                        }
                                    >
                                        {translate(t => t.languageAndMarkedSelector.ctaContinue, "Continue")}
                                    </Button>
                                </section>
                            </Container>
                        </motion.div>
                    </>
                ) : null}
            </AnimatePresence>
        </>
    );
}
